// Shared types and helpers for the DOCX and PDF exports. The two
// renderers target different libraries (docx vs jspdf) so the heavy
// lifting stays per-file; this module just collects the bits that
// would otherwise drift out of sync — labels, slug normaliser,
// key-fact parsing, and the per-closed-volume payload shape.

import type {
  Case,
  CaseRecord,
  CaseVolume,
  Contact,
  CostsSummary,
  KeyFact,
  Significance,
} from "../types";
import { SIGNIFICANCE_LABELS } from "../types";
import type { CaseOverview } from "./caseOverview";

export interface VolumeBriefEntry {
  volume: CaseVolume;
  summary: string | null;
  keyFacts: KeyFact[];
}

export interface ExportBundle {
  case: Case;
  records: CaseRecord[];
  contacts: Contact[];
  // All volumes for the case. Single-volume cases pass a single entry
  // here; renderers suppress the volume summaries section when length
  // <= 1 so the brief doesn't get a hollow heading.
  volumes: CaseVolume[];
  // Closed-volume payloads only — open volumes have no summary. Keyed
  // implicitly by ordering, not by id, because the renderer iterates.
  volumeBriefs: VolumeBriefEntry[];
  // Running tally of AI regenerations that happened while preparing
  // this bundle (summary, story, key_facts). Shown in the progress
  // modal so the user understands usage impact.
  aiCallsUsed?: number;
  // Pre-rendered chronological narrative produced by
  // renderProseFromEvents(). Non-null when the case has a cached
  // timeline to format from; null when the timeline has never been
  // generated — renderers show a placeholder in that case. Legacy
  // `case.story` is no longer consulted by the export layer.
  narrative?: string | null;
  // Cached cost scan, when one has been generated. Rendered as the
  // Cost Summary section of the dossier.
  costs?: CostsSummary | null;
  // Cover line for the dossier — a deterministic fact count
  // ("133 verified facts from 46 records"). Replaces the former AI-summary
  // validation badge, which no longer applies to a fact-based dossier.
  coverFactLine?: string | null;
  // Deterministic, fact-based Case Overview (parties, key events, coverage
  // terms, financial total, commitments). Replaces the former AI summary.
  overview?: CaseOverview | null;
}

export const KEY_FACT_TYPE_LABEL: Record<KeyFact["type"], string> = {
  amount: "Amount",
  commitment: "Commitment",
  deadline: "Deadline",
  admission: "Admission",
  denial: "Denial",
  person: "Person",
  "reference-number": "Reference number",
};

// Date display wrappers. Delegate to the shared UTC-safe formatters
// in lib/formatDate so exports never disagree with the in-app views,
// and neither ever shifts a date west of its storage value. The
// "—" em-dash fallback is retained here because exports use these
// directly in table cells and want a visible placeholder on null.
import {
  formatDate as _formatDate,
  formatDateTime as _formatDateTime,
} from "./formatDate";

export function formatLong(iso: string | null | undefined): string {
  if (!iso) return "—";
  return _formatDate(iso, "long") || iso;
}

export function formatShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  return _formatDate(iso, "short") || iso;
}

export function formatDateTime(iso: string): string {
  return _formatDateTime(iso) || iso;
}

export function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "case"
  );
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function parseKeyFacts(caseData: Case): KeyFact[] {
  if (!caseData.key_facts) return [];
  try {
    const parsed = JSON.parse(caseData.key_facts);
    return Array.isArray(parsed) ? (parsed as KeyFact[]) : [];
  } catch {
    return [];
  }
}

export function typeLabelFor(r: CaseRecord): string {
  const m = r.file_mime_type ?? "";
  if (r.source_type === "email") return "Email";
  if (r.source_type === "video" || m.startsWith("video/")) return "Video";
  if (r.source_type === "phone_call" || m.startsWith("audio/")) return "Audio";
  if (m.startsWith("image/")) return "Screenshot";
  if (m === "application/pdf") return "PDF";
  if (m.includes("wordprocessingml")) return "Word";
  if (m.includes("spreadsheetml")) return "Excel";
  if (r.file_r2_key) return "File";
  if (r.source_type === "letter") return "Letter";
  if (r.source_type === "sms") return "SMS";
  if (r.source_type === "in_person") return "In person";
  return "Other";
}

export function directionLabel(r: CaseRecord): string {
  return r.direction === "inbound" ? "In" : "Out";
}

export function significanceLabel(s: Significance): string {
  return SIGNIFICANCE_LABELS[s] ?? s;
}

// Actual first/last record_date for records assigned to a specific
// volume. The volumes table carries its own `first_record_date` /
// `last_record_date` columns, but those reflect volume lifecycle
// timestamps (creation, last update) — not the content span. For
// reporting we want content span.
export function volumeDateRange(
  records: CaseRecord[],
  volumeId: string,
): { first: string | null; last: string | null } {
  const dates = records
    .filter((r) => r.volume_id === volumeId)
    .map((r) => r.record_date)
    .filter((d): d is string => !!d)
    .sort();
  if (dates.length === 0) return { first: null, last: null };
  return { first: dates[0]!, last: dates[dates.length - 1]! };
}

export function dateRangeLine(records: CaseRecord[]): string {
  const dates = records
    .map((r) => r.record_date)
    .filter((d): d is string => !!d)
    .sort();
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (!first || !last) return "No records yet";
  if (first === last) return `Records on ${formatLong(first)}`;
  return `Records from ${formatLong(first)} to ${formatLong(last)}`;
}

// Label shown on record bookmarks in the PDF appendix. Kept short
// enough to read in a PDF outline panel — ~60 chars.
export function recordBookmarkLabel(
  index: number,
  r: CaseRecord,
): string {
  const date = formatShort(r.record_date);
  const type = typeLabelFor(r);
  const fromRaw = (r.from_addr ?? "").trim();
  const fromShort =
    fromRaw.length > 30 ? `${fromRaw.slice(0, 29)}…` : fromRaw;
  const from = fromShort ? ` from ${fromShort}` : "";
  return `Record ${index} — ${date} — ${type}${from}`;
}

// ---------------------------------------------------------------------------
// Redesigned-export helpers — staleness, parties, status, AI disclaimer,
// record-reference regex, image fetching for PDF Appendix B.
// ---------------------------------------------------------------------------

// Cached volume summaries generated before the timezone fix shipped
// have narrative timestamps ("...at 2:00:33 PM CDT...") baked into
// the stored text. Display-time scrub: strip "at HH:MM[:SS] AM/PM
// TZ" patterns so the rendered narrative reads cleanly. We don't
// mutate the cache — the next AI regeneration produces clean text
// natively, and the stored form may still carry useful context
// elsewhere in the app.
const TIMESTAMP_IN_PROSE_RE =
  /\s+at\s+\d{1,2}:\d{2}(?::\d{2})?\s*(?:AM|PM|am|pm)(?:\s+[A-Z]{2,5})?/g;

export function stripProseTimestamps(text: string | null): string {
  if (!text) return "";
  return text.replace(TIMESTAMP_IN_PROSE_RE, "");
}

export const AI_DISCLAIMER =
  "This document was generated with AI assistance by CaseThread. " +
  "AI-generated summaries, narratives, and analysis may contain errors. " +
  "All facts should be independently verified. This document does not " +
  "constitute legal advice.";

export function copyrightLine(): string {
  // UTC year so the stamp matches the date printed on the rest of
  // the document — both come from the same wall clock.
  const year = new Date().getUTCFullYear();
  return `© ${year} CaseThread`;
}

// Latest record-modification signal. Records don't have an explicit
// updated_at column, so we use MAX(logged_at) as a proxy. Edits flip
// is_edited and rewrite body_text but keep logged_at pinned to the
// original ingest moment — still a reasonable staleness trigger
// because the dominant staleness driver is "new records arrived".
export function latestRecordChangeIso(
  records: CaseRecord[],
): string | null {
  let latest: string | null = null;
  for (const r of records) {
    const t = r.logged_at ?? null;
    if (!t) continue;
    if (!latest || t > latest) latest = t;
  }
  return latest;
}

export interface ExportStaleness {
  summary: boolean;
  story: boolean;
  keyFacts: boolean;
  latestRecord: string | null;
}

// Compare each cached piece's updated_at against latest record change.
// True = the cache predates the newest record and needs regeneration
// before export. Missing caches (no summary/story/key_facts at all)
// also count as stale.
export function computeStaleness(
  caseData: Case,
  records: CaseRecord[],
): ExportStaleness {
  const latestRecord = latestRecordChangeIso(records);
  const isStale = (cacheTs: string | null | undefined): boolean => {
    if (!cacheTs) return true;
    if (!latestRecord) return false;
    return cacheTs < latestRecord;
  };
  return {
    summary: isStale(caseData.summary_updated_at) || !caseData.summary,
    story: isStale(caseData.story_updated_at) || !caseData.story,
    keyFacts:
      isStale(caseData.key_facts_updated_at) || !caseData.key_facts,
    latestRecord,
  };
}

// Lightweight party roll-up, used by the "Parties Involved" section.
// Union of contacts (structured: name + role + email) and raw
// from_addr / to_addr addresses harvested from every record, with
// fuzzy dedup so "Gordon" and "Gordon Macpherson <gm@x.com>" collapse
// into a single row.
export interface PartySummary {
  name: string;
  role: string | null;
  email: string | null;
  // Variant forms of this party seen across records — shown in
  // parentheses so the reader can trace how the system collapsed
  // them. "Gordon" + "gm@x.com" when the canonical is
  // "Gordon Macpherson". Empty when only one form appeared.
  aliases: string[];
  mentions: number;
}

// "@casethread.app" addresses are the per-case forwarding inbox —
// internal plumbing, never a real party. Dropped from the parties
// list entirely.
function isCaseThreadEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return /@casethread\.app$/i.test(email.trim());
}

// Parse a raw address entry into a (name, email, role) triple.
// Handles the shapes the email/record pipeline actually emits:
//   "Full Name <user@host>"
//   "\"Full Name\" <user@host>"
//   "user@host"
//   "Full Name"
//   "Full Name, Role Title, Division"  — anything after the first
//       comma is treated as the role (honorifics aside).
function parseAddressEntry(raw: string): {
  name: string | null;
  email: string | null;
  role: string | null;
} {
  const t = raw.trim();
  if (!t) return { name: null, email: null, role: null };
  const angle = t.match(/^(.*?)\s*<([^>]+)>\s*$/);
  if (angle) {
    const [namePart, rolePart] = splitNameAndRole(
      angle[1]!.trim().replace(/^"(.*)"$/, "$1").trim(),
    );
    const email = angle[2]!.trim().toLowerCase();
    return { name: namePart || null, email, role: rolePart };
  }
  if (/^[\w.+-]+@[\w.-]+\.\w+$/.test(t)) {
    return { name: null, email: t.toLowerCase(), role: null };
  }
  const [namePart, rolePart] = splitNameAndRole(t);
  return { name: namePart, email: null, role: rolePart };
}

// "Scott Prochnow, Sr. Manager, Operations" → ["Scott Prochnow",
// "Sr. Manager, Operations"]. Everything past the first comma
// lands in the role; no comma → everything is a name.
function splitNameAndRole(raw: string): [string, string | null] {
  const idx = raw.indexOf(",");
  if (idx === -1) return [raw.trim(), null];
  const name = raw.slice(0, idx).trim();
  const role = raw.slice(idx + 1).trim();
  return [name, role || null];
}

// Explode a compound address-line like
//   "Scott Prochnow <sp@x.com> • cc: Ian <ik@x.com>, Patty <pa@x.com>"
// into individual ["Scott Prochnow <sp@x.com>", "Ian <ik@x.com>",
// "Patty <pa@x.com>"] so each goes through dedup separately. Safe
// for simple single-party entries too — they just pass through.
function explodeAddressLine(line: string): string[] {
  const parts = line.split(/\s+•\s+cc:\s+/i);
  if (parts.length === 1) return [line.trim()];
  const main = parts[0]?.trim();
  const out: string[] = [];
  if (main) out.push(main);
  // The cc tail can itself be a list of comma-separated recipients.
  // Split on ", " but avoid splitting inside quoted names or angle
  // brackets. Simple heuristic: only split on commas outside <…>.
  for (let i = 1; i < parts.length; i++) {
    const cc = parts[i]!;
    let depth = 0;
    let buf = "";
    for (const ch of cc) {
      if (ch === "<") depth++;
      else if (ch === ">") depth--;
      else if (ch === "," && depth === 0) {
        if (buf.trim()) out.push(buf.trim());
        buf = "";
        continue;
      }
      buf += ch;
    }
    if (buf.trim()) out.push(buf.trim());
  }
  return out;
}

// Strip honorifics, suffixes, and collapse whitespace. Result is a
// lower-case, single-spaced comparable form.
function normaliseName(name: string): string {
  return name
    .toLowerCase()
    .replace(/^["']+|["']+$/g, "")
    .replace(/^(mr|mrs|ms|miss|dr|prof|sir|lord|lady)\.?\s+/i, "")
    .replace(/\s+(jr|sr|ii|iii|iv|phd|md|esq)\.?$/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

function firstToken(name: string): string {
  const parts = normaliseName(name).split(" ");
  return parts[0] ?? "";
}

// "Specificity" heuristic used when picking which variant becomes the
// canonical name. Multi-token names beat single-token; longer strings
// beat shorter; proper case beats lower-case (lets "Gordon Macpherson"
// win over "gordon macpherson"). Emails lose to names outright.
function nameSpecificity(name: string): number {
  if (!name) return -1;
  const tokens = normaliseName(name).split(" ").filter(Boolean).length;
  const hasCase = /[A-Z]/.test(name);
  return tokens * 100 + name.length + (hasCase ? 5 : 0);
}

interface PartyGroup {
  // Canonical display name — re-evaluated as we merge more entries.
  // Chosen as the most-frequent casing of the most-specific form so
  // "Macpherson" vs "MacPherson" resolves to whichever the user
  // actually types most often.
  name: string;
  // Frequency of each specific name form (case-sensitive). Used to
  // pick the canonical display when multiple casings exist for the
  // same person.
  nameCounts: Map<string, number>;
  // All non-duplicate surface forms seen, case-insensitive keyed.
  // Shown to the user as "(also: X, Y)" in parties tables.
  variants: Map<string, string>; // normKey → original form
  email: string | null;
  role: string | null;
  mentions: number;
}

function makeGroup(
  name: string,
  email: string | null,
  role: string | null,
): PartyGroup {
  const g: PartyGroup = {
    name,
    nameCounts: new Map(),
    variants: new Map(),
    email,
    role,
    mentions: 0,
  };
  bumpNameCount(g, name);
  return g;
}

function bumpNameCount(g: PartyGroup, name: string): void {
  if (!name) return;
  g.nameCounts.set(name, (g.nameCounts.get(name) ?? 0) + 1);
}

// Pick the best canonical name from the tallied variants. Primary
// signal: specificity (multi-token > single-token, longer > shorter).
// Tiebreaker: frequency — the form the user actually writes most
// often. This resolves "MacPherson" vs "Macpherson" to whichever
// appears more in the data rather than alphabetically arbitrary.
function pickCanonicalName(g: PartyGroup): string {
  let best = g.name;
  let bestScore = -Infinity;
  for (const [form, count] of g.nameCounts) {
    const score = nameSpecificity(form) * 1000 + count;
    if (score > bestScore) {
      bestScore = score;
      best = form;
    }
  }
  return best;
}

function mergeInto(g: PartyGroup, other: Partial<PartyGroup>): void {
  if (other.name) {
    bumpNameCount(g, other.name);
    g.name = pickCanonicalName(g);
  }
  if (!g.email && other.email) g.email = other.email;
  if (!g.role && other.role) g.role = other.role;
  if (other.mentions) g.mentions += other.mentions;
}

function addVariant(g: PartyGroup, raw: string): void {
  const clean = raw.trim();
  if (!clean) return;
  const key = clean.toLowerCase();
  if (!g.variants.has(key)) g.variants.set(key, clean);
}

// Last name = last space-separated token of the normalised form.
// Used for middle-name merges: "gordon macpherson" and
// "gordon reid macpherson" both resolve to last="macpherson" +
// first="gordon", so they match.
function lastToken(name: string): string {
  const parts = normaliseName(name).split(" ").filter(Boolean);
  return parts[parts.length - 1] ?? "";
}

// Edit-distance for fuzzy first-name matching. Two-row dynamic
// program over the two input strings — small strings (≤30 chars
// for a typical name) so the allocation cost is fine. Used to
// collapse spelling variants like "Patti/Patty", "Stephen/Steven",
// "Jon/John" when they share an exact last name.
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const n = b.length;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + cost,
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

export function extractParties(
  records: CaseRecord[],
  contacts: Contact[],
): PartySummary[] {
  interface RawEntry {
    name: string | null;
    email: string | null;
    role: string | null;
    surface: string;
    mentions: number;
  }
  const rawEntries: RawEntry[] = [];

  for (const c of contacts) {
    if (isCaseThreadEmail(c.email)) continue;
    rawEntries.push({
      name: c.name?.trim() || null,
      email: c.email?.trim().toLowerCase() || null,
      role: c.role?.trim() || null,
      surface: c.name?.trim() || c.email?.trim() || "",
      mentions: 0,
    });
  }
  // Record from/to addresses may be compound "X • cc: Y, Z" strings.
  // Explode first so each person counts independently, then parse +
  // comma-split name-from-role per piece.
  for (const r of records) {
    for (const addr of [r.from_addr, r.to_addr]) {
      if (!addr || !addr.trim()) continue;
      for (const piece of explodeAddressLine(addr)) {
        const parsed = parseAddressEntry(piece);
        if (isCaseThreadEmail(parsed.email)) continue;
        if (!parsed.name && !parsed.email) continue;
        // When a role was extracted, fold the parsed name back into
        // the surface so the alias list doesn't carry the full
        // "Name, Role, Division" string — the role already lives in
        // its own column.
        const surface =
          parsed.role && parsed.name
            ? (parsed.email
                ? `${parsed.name} <${parsed.email}>`
                : parsed.name)
            : piece;
        rawEntries.push({
          name: parsed.name,
          email: parsed.email,
          role: parsed.role,
          surface,
          mentions: 1,
        });
      }
    }
  }

  // Pass 1 — group by email (case-insensitive; emails land pre-
  // lowercased from parseAddressEntry / Contact accessor). Each
  // unique email gets its own group.
  const groupsByEmail = new Map<string, PartyGroup>();
  const nameOnlyEntries: RawEntry[] = [];
  for (const e of rawEntries) {
    if (e.email) {
      let g = groupsByEmail.get(e.email);
      if (!g) {
        g = makeGroup(e.name || e.email, e.email, e.role);
        groupsByEmail.set(e.email, g);
      }
      mergeInto(g, {
        name: e.name ?? undefined,
        role: e.role ?? undefined,
        mentions: e.mentions,
      });
      addVariant(g, e.surface);
    } else {
      nameOnlyEntries.push(e);
    }
  }

  // Pass 2 — merge email groups that share a (first + last token)
  // name signature. Handles middle-name variants ("Gordon Macpherson"
  // vs "Gordon Reid MacPherson") that ended up in separate email
  // groups — typically when one form has no email and another does.
  // Also case-insensitive by design: normaliseName already lowercases
  // so "MacPherson" vs "Macpherson" collide.
  function nameSignature(name: string): string {
    const norm = normaliseName(name);
    const parts = norm.split(" ").filter(Boolean);
    if (parts.length < 2) return norm;
    return `${parts[0]} ${parts[parts.length - 1]}`;
  }
  const sigToEmails = new Map<string, string[]>();
  for (const [email, g] of groupsByEmail) {
    const sig = nameSignature(g.name);
    if (!sig || sig.split(" ").length < 2) continue;
    const list = sigToEmails.get(sig) ?? [];
    list.push(email);
    sigToEmails.set(sig, list);
  }
  for (const [, emails] of sigToEmails) {
    if (emails.length < 2) continue;
    // Merge all emails under the signature into the first group.
    // Subsequent emails are removed from the map but their variants
    // are folded into the primary group.
    const primary = groupsByEmail.get(emails[0]!)!;
    for (let i = 1; i < emails.length; i++) {
      const sibling = groupsByEmail.get(emails[i]!);
      if (!sibling) continue;
      mergeInto(primary, {
        name: sibling.name,
        role: sibling.role ?? undefined,
        mentions: sibling.mentions,
      });
      // Copy every variant + name-frequency tally from the sibling.
      for (const [k, v] of sibling.variants) {
        if (!primary.variants.has(k)) primary.variants.set(k, v);
      }
      for (const [form, count] of sibling.nameCounts) {
        primary.nameCounts.set(
          form,
          (primary.nameCounts.get(form) ?? 0) + count,
        );
      }
      // Keep the extra email on the record as an alias — users
      // who see two emails for the same person need to know both.
      if (sibling.email) addVariant(primary, sibling.email);
      groupsByEmail.delete(emails[i]!);
    }
    primary.name = pickCanonicalName(primary);
  }

  // Pass 3 — fold name-only entries into email groups when a single
  // email group matches. Match order:
  //   - exact full-name match (normalised)
  //   - first + last token signature match (handles middle names)
  //   - bare first-name match when exactly one email group's first
  //     token matches (and no other candidate exists)
  const nameGroups = new Map<string, PartyGroup>();
  for (const e of nameOnlyEntries) {
    const targetName = e.name;
    if (!targetName) continue;
    const targetNorm = normaliseName(targetName);
    const targetSig = nameSignature(targetName);
    const tokenCount = targetNorm.split(" ").filter(Boolean).length;
    const firstTok = firstToken(targetName);

    const emailCandidates = Array.from(groupsByEmail.values()).filter(
      (g) => {
        if (!g.name) return false;
        const gNorm = normaliseName(g.name);
        if (targetNorm === gNorm) return true;
        if (
          tokenCount >= 2 &&
          nameSignature(g.name) === targetSig
        )
          return true;
        if (tokenCount === 1 && firstToken(g.name) === firstTok) return true;
        return false;
      },
    );

    if (emailCandidates.length === 1) {
      const g = emailCandidates[0]!;
      mergeInto(g, {
        name: targetName,
        role: e.role ?? undefined,
        mentions: e.mentions,
      });
      addVariant(g, e.surface);
      continue;
    }

    // No unambiguous email match — bucket by normalised full name so
    // repeated bare "Gordon"s collapse to a single standalone row.
    // Key by signature when we have a full name so "Gordon" and
    // "Gordon Macpherson" standalone entries still collide.
    const key =
      tokenCount >= 2 ? `sig:${targetSig}` : `name:${targetNorm}`;
    let g = nameGroups.get(key);
    if (!g) {
      g = makeGroup(targetName, null, e.role ?? null);
      nameGroups.set(key, g);
    }
    mergeInto(g, {
      name: targetName,
      role: e.role ?? undefined,
      mentions: e.mentions,
    });
    addVariant(g, e.surface);
  }

  // Pass 3.5 — fuzzy first-name merge. Two groups share the same
  // exact last name AND their first names differ by Levenshtein
  // distance ≤ 2 → probably the same person (Patti/Patty,
  // Stephen/Steven, Jon/John, Kathy/Cathy). We require an exact
  // last-name match so "John Smith" and "Jon Baker" don't collapse.
  // Winner is the higher-mentions group; ties favour the first
  // seen. Single-token names are excluded — too much risk of
  // collapsing "John" into "Jon Baker" or similar.
  {
    const allGroups = [
      ...groupsByEmail.values(),
      ...nameGroups.values(),
    ];
    const dropped = new Set<PartyGroup>();
    for (let i = 0; i < allGroups.length; i++) {
      const a = allGroups[i]!;
      if (dropped.has(a)) continue;
      const aParts = normaliseName(a.name).split(" ").filter(Boolean);
      if (aParts.length < 2) continue;
      const aFirst = aParts[0]!;
      const aLast = aParts[aParts.length - 1]!;
      for (let j = i + 1; j < allGroups.length; j++) {
        const b = allGroups[j]!;
        if (dropped.has(b)) continue;
        const bParts = normaliseName(b.name).split(" ").filter(Boolean);
        if (bParts.length < 2) continue;
        const bLast = bParts[bParts.length - 1]!;
        if (aLast !== bLast) continue;
        const bFirst = bParts[0]!;
        const dist = levenshtein(aFirst, bFirst);
        if (dist === 0 || dist > 2) continue;
        const [winner, loser] =
          a.mentions >= b.mentions ? [a, b] : [b, a];
        mergeInto(winner, {
          name: loser.name,
          role: loser.role ?? undefined,
          mentions: loser.mentions,
        });
        for (const [k, v] of loser.variants) {
          if (!winner.variants.has(k)) winner.variants.set(k, v);
        }
        for (const [form, count] of loser.nameCounts) {
          winner.nameCounts.set(
            form,
            (winner.nameCounts.get(form) ?? 0) + count,
          );
        }
        if (loser.email) addVariant(winner, loser.email);
        // Keep the winner's email as canonical; the loser's email
        // was already carried into the variants list above.
        winner.name = pickCanonicalName(winner);
        dropped.add(loser);
      }
    }
    // Remove dropped groups from their source maps so the final
    // flatten doesn't re-emit them.
    for (const [k, g] of groupsByEmail) {
      if (dropped.has(g)) groupsByEmail.delete(k);
    }
    for (const [k, g] of nameGroups) {
      if (dropped.has(g)) nameGroups.delete(k);
    }
  }

  // Pass 4 — flatten to PartySummary[]. Aliases = variants minus
  // the canonical display form (case-insensitive) and minus any
  // "Name, Role" combos that the role extraction already handled.
  // Also strip entries that are prefixes of the canonical
  // ("Gordon" when canonical is "Gordon Macpherson") since the
  // reader can infer those.
  const all = [...groupsByEmail.values(), ...nameGroups.values()];
  const out: PartySummary[] = all.map((g) => {
    const canonicalKey = g.name.toLowerCase();
    const canonicalNorm = normaliseName(g.name);
    const roleLower = g.role?.trim().toLowerCase() ?? null;
    const aliases: string[] = [];
    for (const [k, v] of g.variants) {
      if (k === canonicalKey) continue;
      if (v.trim().toLowerCase() === canonicalKey) continue;
      // "Scott Prochnow, Sr. Manager, Operations" when canonical is
      // "Scott Prochnow" and role is already captured separately.
      if (roleLower) {
        const roleSuffix = `${canonicalKey}, ${roleLower}`;
        if (v.trim().toLowerCase() === roleSuffix) continue;
        // Also catch "<canonical>, <role-with-extra-commas>" where
        // the role itself has commas; the surface-as-whole equals
        // the role suffix after the name.
        if (v.toLowerCase().startsWith(`${canonicalKey}, `)) {
          const tail = v.slice(canonicalKey.length + 2).trim().toLowerCase();
          if (tail === roleLower) continue;
        }
      }
      // First-name prefix of the canonical is redundant noise.
      if (
        normaliseName(v) === firstToken(g.name) &&
        canonicalNorm.split(" ").length > 1
      ) {
        continue;
      }
      aliases.push(v);
    }
    const seen = new Set<string>();
    const uniqueAliases: string[] = [];
    for (const a of aliases) {
      const ak = a.toLowerCase();
      if (seen.has(ak)) continue;
      seen.add(ak);
      uniqueAliases.push(a);
    }
    return {
      name: g.name,
      role: g.role,
      email: g.email,
      aliases: uniqueAliases,
      mentions: g.mentions,
    };
  });

  return out.sort(
    (a, b) => b.mentions - a.mentions || a.name.localeCompare(b.name),
  );
}

// Current-status paragraph derived from structured case fields +
// most-recent record activity. Natural prose, not a labeled list.
// Priority order:
//   1. If a closed volume has a resolution summary, lead with it.
//      (Callers pass volumeBriefs as the second arg when available.)
//   2. Status sentence mentioning deadline if set.
//   3. Last real activity — non-manual records preferred, since a
//      manually-written "my note" is usually user housekeeping, not
//      genuine inbound/outbound communication.
//   4. Next expected action: prefer a deadline from key_facts
//      (type "deadline"); fall back to inbound-vs-outbound heuristic.
export function buildStatusParagraph(
  caseData: Case,
  records: CaseRecord[],
  volumeBriefs: VolumeBriefEntry[] = [],
): string {
  const sentences: string[] = [];

  // 1. Closed-volume resolution lead — ONLY when the case itself is
  // actually resolved. For open/waiting/escalated cases the volume
  // summary describes a prior chapter, not the current status, and
  // using it as the lead misrepresents where the case stands.
  if (caseData.status === "resolved") {
    const closedBriefs = volumeBriefs
      .filter((v) => v.volume.closed_at !== null && v.summary?.trim())
      .sort((a, b) =>
        (b.volume.closed_at ?? "").localeCompare(a.volume.closed_at ?? ""),
      );
    if (closedBriefs.length > 0) {
      const b = closedBriefs[0]!;
      const cleaned = stripProseTimestamps(b.summary);
      const headline = cleaned.trim().split(/(?<=[.!?])\s+/)[0] ?? "";
      if (headline) sentences.push(headline);
    }
  }

  // 2. Current status + deadline.
  const statusSentence =
    caseData.status === "resolved"
      ? "The case is marked resolved."
      : caseData.status === "escalated"
        ? "The case is currently escalated."
        : caseData.status === "waiting"
          ? "The case is waiting on a response."
          : "The case is currently open.";
  if (caseData.deadline) {
    sentences.push(
      `${statusSentence} A deadline is on file for ${formatLong(caseData.deadline)}.`,
    );
  } else {
    sentences.push(statusSentence);
  }

  // 3. Last real activity — prefer non-manual records. Falls back
  // to any record (including manual) if the case has nothing else.
  const chronological = [...records].sort((a, b) =>
    a.record_date.localeCompare(b.record_date),
  );
  const lastReal =
    [...chronological]
      .reverse()
      .find((r) => r.creation_source !== "manual") ??
    chronological[chronological.length - 1];
  if (lastReal) {
    const when = formatLong(lastReal.record_date);
    const rawSubject = lastReal.subject?.trim() || "";
    const sender = lastReal.from_addr?.trim();
    const recipient = lastReal.to_addr?.trim();
    const isOutbound = lastReal.direction === "outbound";

    // Skip filename-like "subjects" (IMG_2689.jpg, scan.pdf, etc.).
    // These leak through when the upload wizard defaulted to the
    // filename because the user never set a real subject — using
    // them as narrative descriptors reads terribly.
    const looksLikeFilename = /\.(?:jpe?g|png|gif|webp|heic|heif|bmp|tiff?|pdf|docx?|xlsx?|pptx?|txt|csv|mp3|m4a|wav|ogg|webm|mp4|mov|mkv)$/i.test(
      rawSubject,
    );
    const subject =
      rawSubject && !looksLikeFilename ? rawSubject : "";

    // Short body snippet as a second-choice descriptor. Pull the
    // first line/segment so we don't spill a whole email body into
    // the status paragraph. Cap at ~60 chars, word-boundary cut.
    const bodyRaw = (lastReal.body_text ?? lastReal.ocr_text ?? "")
      .trim();
    const bodyFirstLine =
      bodyRaw.split(/\r?\n/).find((line) => line.trim().length > 0) ?? "";
    const bodySnippet = (() => {
      const s = bodyFirstLine.trim();
      if (!s) return "";
      if (s.length <= 60) return s;
      const cut = s.slice(0, 60);
      const lastSpace = cut.lastIndexOf(" ");
      return (lastSpace > 30 ? cut.slice(0, lastSpace) : cut).trim() + "…";
    })();

    // Outbound = case owner; "from yourself" reads oddly in a
    // third-person document, so we drop the "from X" clause there.
    let sentence: string;
    if (subject && isOutbound) {
      sentence = `The most recent activity was "${subject}" on ${when}.`;
    } else if (subject && sender) {
      sentence = `The most recent activity was "${subject}" from ${sender} on ${when}.`;
    } else if (subject) {
      sentence = `The most recent activity was "${subject}" on ${when}.`;
    } else if (bodySnippet && isOutbound) {
      sentence = `The most recent activity on ${when} was "${bodySnippet}".`;
    } else if (bodySnippet && sender) {
      sentence = `The most recent activity on ${when} was "${bodySnippet}" from ${sender}.`;
    } else if (bodySnippet) {
      sentence = `The most recent activity on ${when} was "${bodySnippet}".`;
    } else if (isOutbound) {
      sentence = recipient
        ? `The most recent activity was outbound correspondence to ${recipient} on ${when}.`
        : `The most recent activity was outbound correspondence on ${when}.`;
    } else if (sender) {
      sentence = `The most recent activity was inbound correspondence from ${sender} on ${when}.`;
    } else {
      sentence = `The most recent activity was on ${when}.`;
    }
    sentences.push(sentence);
  }

  // 4. Next expected action. Prefer a deadline key fact (AI-extracted
  // "respond by X" / "reply required by Y" etc.) over the generic
  // inbound/outbound heuristic.
  const keyFacts = parseKeyFacts(caseData);
  const deadlineFact = keyFacts.find((f) => f.type === "deadline");
  if (deadlineFact) {
    sentences.push(`Next expected action: ${deadlineFact.fact.trim()}`);
  } else if (lastReal) {
    if (lastReal.direction === "inbound") {
      sentences.push(
        "Next expected action: the case owner owes a response.",
      );
    } else {
      sentences.push(
        "Next expected action: awaiting a reply from the counterparty.",
      );
    }
  }

  return sentences.join(" ");
}

// Regex + helper for "(Record #N)" / "Record #N" references in
// AI-generated prose. The PDF turns each match into a clickable
// internal link to Appendix A. DOCX and plain-text readers just see
// the text as-is.
export const RECORD_REF_RE = /Record\s*#(\d+)/gi;

// Auth-aware image fetcher for PDF embedding. Returns a data URL +
// dimensions suitable for jsPDF's addImage(). Non-image MIME types
// return null — the exporter falls back to a "see archive export"
// note in that case. Uses the same auth pattern the ZIP exporter
// uses: credentials: include + any Bearer token the caller threads
// through via authHeaders.
export interface FetchedImage {
  dataUrl: string;
  mime: string;
  // Rendered dimensions chosen at fetch time. Actual natural
  // dimensions are derived from an Image() probe so jsPDF can pick
  // an aspect-correct placement.
  naturalWidth: number;
  naturalHeight: number;
}

// Auth-aware raw-bytes fetcher — used for non-image attachments the
// exporter needs to process client-side (e.g. PDFs rendered to images
// via pdfjs). Returns the bytes + reported MIME, or null on any failure
// so the caller can note "[Attachment could not be loaded]" inline
// without aborting the whole export.
export async function fetchBinary(
  url: string,
  authHeaders: Record<string, string>,
): Promise<{ bytes: ArrayBuffer; mime: string } | null> {
  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: authHeaders,
    });
    if (!res.ok) return null;
    const mime = res.headers.get("content-type") ?? "";
    const bytes = await res.arrayBuffer();
    return { bytes, mime };
  } catch {
    return null;
  }
}

export async function fetchImageAsDataUrl(
  url: string,
  authHeaders: Record<string, string>,
): Promise<FetchedImage | null> {
  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: authHeaders,
    });
    if (!res.ok) return null;
    const mime = res.headers.get("content-type") ?? "";
    if (!mime.startsWith("image/")) return null;
    const blob = await res.blob();
    // Data URL for jsPDF.addImage() — it accepts "data:image/..." strings
    // directly and figures out the format from the prefix.
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("reader_error"));
      reader.onload = () =>
        typeof reader.result === "string"
          ? resolve(reader.result)
          : reject(new Error("reader_not_string"));
      reader.readAsDataURL(blob);
    });
    // Probe natural dimensions via an Image() in memory. This gives us
    // the intrinsic aspect ratio so the PDF placement fits the page
    // width without stretching.
    const dims = await new Promise<{ w: number; h: number }>((resolve) => {
      const img = new Image();
      img.onload = () =>
        resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve({ w: 800, h: 600 });
      img.src = dataUrl;
    });
    return {
      dataUrl,
      mime,
      naturalWidth: dims.w,
      naturalHeight: dims.h,
    };
  } catch {
    return null;
  }
}
